import { Input, Spin, Tooltip, message } from 'antd';
import {
  CloseAiIcon,
  EditableIcon,
  UpdateIcon,
  CopiesIcon,
  ClockIcon,
  PinIcon,
} from '../../../../icons';
import {
  formatEncounterStatus,
  getFullEncounterStatus,
} from '../../utils/encounterHelper';
import { ProfileLogo } from '../baseComponents';
import DeleteEncounter from './DeleteEncounter';
import TriggerPrintEncounter from './TriggerPrintEncounter';
import moment from 'moment';
import { HumanScribeStatus } from './HumanScribeStatus';
import { AudioOutlined, SignatureOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { EncounterPatient } from './AddPatientModalV2';
import { ehrSubmitDocument } from 'src/modules/EHR/store/slices/ehr';
import { LoadingBar } from '../baseComponents/LoadingBar';
import { QrCode01 } from '@insight-health-ai/roseate/icons';

const TooltipContent = ({ record }) => {
  const [copyText, setCopyText] = useState('Copy');

  const getLocalTime = (timeString) => {
    if (!timeString || !record.created_at) return timeString;
    const date = moment(record.created_at).format('YYYY-MM-DD');
    return moment.utc(`${date} ${timeString}`, 'YYYY-MM-DD h:mm A').local();
  };

  const startTime = getLocalTime(
    record.encounter_metadata?.encounter_start_time
  );
  const endTime = getLocalTime(record.encounter_metadata?.encounter_end_time);

  return (
    <div>
      <div className="text-left">
        <div className="flex justify-between gap-2">
          <span>Started:</span>
          <span>{startTime ? startTime.format('h:mm A') : 'N/A'}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span>Ended:</span>
          <span>{endTime ? endTime.format('h:mm A') : 'N/A'}</span>
        </div>
      </div>
      <div className="h-[1px] bg-gray-200 my-2" />
      <div
        onClick={(e) => {
          const duration =
            record.duration > 60
              ? `${Math.floor(record.duration / 60)}h ${record.duration % 60}m`
              : `${record.duration} min`;
          const timeInfo = `Started: ${
            startTime ? startTime.format('MMM Do, YYYY h:mm A') : 'N/A'
          }\nEnded: ${
            endTime ? endTime.format('MMM Do, YYYY h:mm A') : 'N/A'
          }\nDuration: ${duration}`;
          navigator.clipboard.writeText(timeInfo);
          e.stopPropagation();
          setCopyText('Copied!');
        }}
        className="flex items-center gap-1 text-gray-600 cursor-pointer"
      >
        <CopiesIcon height="18" width="18" color="#000" />
        <span className="text-sm">{copyText}</span>
      </div>
    </div>
  );
};

//Table Columns
export const columns = ({
  encounterList,
  humanScribeStatus,
  setHoveredRowIndex,
  editingRow,
  setEditingRow,
  handleDescriptionChange,
  handleCancelEdit,
  storedParams,
  handleSaveDescription,
  hoveredRowIndex,
  handleQrCodeOpen,
  searchFilters,
  schedulepage,
  autoChangeStatusToInProgress,
  setCurrentEncounter,
  setRecord,
  setIsStreaming,
  setIsRecording,
  isHumanScribeEntitled,
  encounterPhase,
  editedSummary,
  selectedRecord,
  setSelectedRecord,
  pinnedEncounterIds = [],
  dispatch,
  needsZoomReauth = false,
  navigate,
  setSelectedEncounter,
}) => [
  {
    title: (
      <div className="flex items-center">
        {encounterList?.length > 0 ? '' : ''}
      </div>
    ),
    dataIndex: 'description',
    key: 'description',
    render: (text, record, rowIndex) => {
      let { color, displayStatus } = formatEncounterStatus(record);
      let { fullEncounterStatus, fullEncounterColor } = getFullEncounterStatus(
        record,
        humanScribeStatus
      );

      const editing = editingRow?.encounter_id === record.encounter_id;
      const hovered = hoveredRowIndex === rowIndex;
      const isPinned = pinnedEncounterIds.includes(record.encounter_id);

      return (
        <div
          className="flex flex-col p-2 gap-1"
          onMouseEnter={() => {
            setHoveredRowIndex(rowIndex);
          }}
          onMouseLeave={() => {
            setHoveredRowIndex(null);
          }}
        >
          <div className="flex w-full">
            <ProfileLogo
              name={`${record.practitioner_first_name} ${record.practitioner_last_name}`}
              className={'profile-popover-logo mr-1 sm:mr-2'}
              practitioner_name={record?.practitioner_name}
              practitioner_id={record?.practitioner_id}
            />
            <div className="innerAura flex-grow">
              {editing ? (
                <div className="flex items-center gap-3 w-full">
                  <Input
                    className="edit-input"
                    style={{
                      width: '100%',
                      background: '#ffffff',
                      border: '1px solid #fff',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      borderRadius: '10px',
                      padding: '5px 12px',
                      boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
                    }}
                    defaultValue={editingRow?.description}
                    onChange={(e) => handleDescriptionChange(e, record)}
                  />
                  <div
                    className="bg-[#00d091] rounded-full flex items-center justify-center p-1"
                    style={{ padding: '4px' }}
                  >
                    <UpdateIcon
                      onClick={() =>
                        handleSaveDescription(editingRow, storedParams)
                      }
                    />
                  </div>
                  <div
                    className="bg-[#d63232] rounded-full flex items-center justify-center"
                    style={{ padding: '4px' }}
                  >
                    <CloseAiIcon
                      stroke="#ffffff"
                      fill="#ffffff"
                      className=""
                      onClick={handleCancelEdit}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-[12px] font-medium flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {isPinned && (
                      <Tooltip title="Active encounter" placement="top">
                        <PinIcon fill="#f59e0b" className="w-3 h-3 shrink-0" />
                      </Tooltip>
                    )}
                    <Tooltip
                      title={
                        <span
                          onClick={(e) => {
                            navigator.clipboard.writeText(
                              `${record?.encounter_id}`
                            );
                            e.stopPropagation();
                            message.success('Encounter ID copied!', 1);
                          }}
                          className="cursor-pointer flex items-center gap-[5px]"
                        >
                          <span className="text-[13px]">
                            Encounter ID: {record?.encounter_id}
                          </span>
                          <CopiesIcon height="18" width="18" color="#000" />
                        </span>
                      }
                      align={{ offset: [0, -6] }}
                      placement="top"
                    >
                      {text}
                      {hovered && (
                        <Tooltip
                          title="Edit name"
                          placement="right"
                          align={{ offset: [10, 5] }}
                        >
                          <span
                            style={{ verticalAlign: 'middle' }}
                            className="inline-flex items-center ml-2"
                          >
                            <EditableIcon
                              onClick={() => setEditingRow(record)}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Tooltip>
                  </div>
                  {/* {record?.encounter_metadata?.is_pinned === 'true' && (
                    <Pin02 />
                  )} */}
                </div>
              )}

              <div className="text-gray-500 text-[12px] flex flex-col">
                <div className="flex items-center gap-2">
                  {isHumanScribeEntitled ? (
                    <div className="" style={{ color: fullEncounterColor }}>
                      {fullEncounterStatus}
                    </div>
                  ) : (
                    <div className="" style={{ color }}>
                      {displayStatus}
                    </div>
                  )}
                  {record.duration > 0 && (
                    <Tooltip
                      title={
                        record.encounter_metadata?.encounter_start_time &&
                        record.encounter_metadata?.encounter_end_time ? (
                          <TooltipContent record={record} />
                        ) : null
                      }
                      destroyTooltipOnHide
                    >
                      <span className="bg-white px-1.5 py-0.0 rounded-md border border-gray-200 flex items-center gap-1 text-[12px] text-[#00d091]">
                        <ClockIcon
                          className="w-3 h-3 shrink-0"
                          fill="#00d091"
                        />
                        {record.duration > 60
                          ? `${Math.floor(record.duration / 60)}h ${
                              record.duration % 60
                            }m`
                          : `${record.duration} min`}
                      </span>
                    </Tooltip>
                  )}
                  {record.expires_in_days <= 7 &&
                    record.expires_in_days > 0 && (
                      <span className="text-red-500 text-[8px] font-medium ml-1 px-1 py-0.5 rounded-md bg-red-100 border border-red-200">
                        Expires in {record.expires_in_days} days
                      </span>
                    )}
                  {record.expires_in_days <= 0 && (
                    <span className="text-red-500 text-[8px] font-medium ml-1 px-1 py-0.5 rounded-md bg-red-100 border border-red-200">
                      Expiring today
                    </span>
                  )}
                </div>
                {isHumanScribeEntitled && record?.assigned_scribe_id && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <SignatureOutlined />
                    {record.assigned_scribe_name}
                  </div>
                )}
                <div className="flex items-center gap-1 text-gray-600">
                  <AudioOutlined />
                  {record.practitioner_name}
                </div>

                {record?.start_time && moment(record.start_time).isValid() && (
                  <div>
                    {moment(record.start_time).format('MMM Do, YYYY')}{' '}
                    {moment(record.start_time).format('h:mm A')}
                  </div>
                )}
                {record?.zoom_meeting_id &&
                  (record?.in_visit_status === 'new' ||
                    record?.in_visit_status === 'inprogress') && (
                    <div className="flex items-start gap-2 text-xs">
                      <Spin
                        indicator={
                          <LoadingBar color="#5A8DDA" height="14" width="14" />
                        }
                        size="small"
                      />
                      {needsZoomReauth ? (
                        <div
                          className="flex flex-col gap-1 cursor-pointer"
                          onClick={() => navigate('/integrations')}
                        >
                          <span className="text-[#FF6F00] underline text-xs hover:text-[#FF8C00]">
                            Zoom authentication expired
                          </span>
                        </div>
                      ) : (
                        <span className="text-[#5A8DDA]">
                          Waiting for zoom meeting recording to be available
                        </span>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
          {hovered && <div className="border-t border-gray-200" />}
          <div
            className={`${
              hovered ? 'flex justify-between items-center' : 'hidden'
            } cursor-default`}
          >
            <EncounterPatient encounter={record} />
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {record?.in_visit_status === 'completed' && (
                <div className="flex items-center cursor-pointer">
                  <TriggerPrintEncounter
                    key={record?.encounter_id}
                    record={record}
                    selectedRecord={selectedRecord}
                    setSelectedRecord={setSelectedRecord}
                    editedSummary={editedSummary}
                    setCurrentEncounter={setCurrentEncounter}
                    setRecord={setRecord}
                    setIsStreaming={setIsStreaming}
                    setIsRecording={setIsRecording}
                    autoChangeStatusToInProgress={autoChangeStatusToInProgress}
                    setSelectedEncounter={setSelectedEncounter}
                  />
                </div>
              )}
              <Tooltip title="Show QR code" placement="top">
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => {
                    handleQrCodeOpen(record);
                  }}
                >
                  <QrCode01 className="text-lg" />
                </div>
              </Tooltip>
              <Tooltip title="Remove encounter" placement="topRight">
                <div className="flex items-center cursor-pointer">
                  <DeleteEncounter
                    encounterDetailsSlice={record}
                    searchFilters={searchFilters}
                    record={record}
                    storedParams={storedParams}
                    schedulepage={schedulepage}
                  />
                </div>
              </Tooltip>
              {isHumanScribeEntitled && (
                <div className="flex items-center cursor-pointer">
                  <HumanScribeStatus
                    encounterDetailsSlice={record}
                    encounterPhase={encounterPhase}
                    humanScribeStatus={humanScribeStatus}
                    storedParams={storedParams}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
    onCell: (record) => ({
      onClick: () => {
        setRecord(record);
        setIsStreaming(false);
        dispatch(ehrSubmitDocument(null));
        setIsRecording(false);
        setCurrentEncounter(record);
        autoChangeStatusToInProgress(record);
        setSelectedEncounter?.(record);
      },
    }),
  },
];
